import { useEffect, useMemo, useState } from 'react';
import { useAtomValue } from 'jotai';
import { Box, Stack } from '@mui/material';

import { RiskAssessmentDrawerTable } from '../RiskAssessmentDrawerTable';
import { Matrix } from '../../../Review/Review';
import { reviewTabStyles } from '../../../Review/styles';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import {
  useGetAssembliesRiskAssessmentSummaryQuery,
  RiskType,
  RiskFieldsTypeEnum,
  Maybe,
} from '@/__generated__/graphql';
import { riskAssessmentAssemblyTemplate, selectedAssemblyId } from '@/components/Analysis/state';
import { useDataToGenerateMatrices } from '../../../hooks/useDataToGenerateMatrices';
import { businessLossKey, sheKey, xLabels, yLabels } from '@/constants';

export const RiskMatrixTab = () => {
  const selectedTab = RiskFieldsTypeEnum.Review;
  const template = useAtomValue(riskAssessmentAssemblyTemplate);
  const structureId = useGetStructureId();
  const assemblyId = useAtomValue(selectedAssemblyId);

  const [businessLoss, setBusinessLoss] = useState<Maybe<RiskType>[] | undefined>([]);
  const [she, setShe] = useState<Maybe<RiskType>[] | undefined>([]);

  const { data: riskSummaryData } = useGetAssembliesRiskAssessmentSummaryQuery({
    variables: {
      input: {
        structureId,
        templateId: template?.id!,
        templateFieldKeys: [businessLossKey, sheKey],
        assemblyId,
      },
    },
    fetchPolicy: 'no-cache',
  });

  const riskMatricesData = useMemo(() => {
    if (riskSummaryData) {
      return riskSummaryData?.getAssembliesRiskAssessmentSummary;
    }
    return undefined;
  }, [riskSummaryData]);

  useEffect(() => {
    if (riskMatricesData) {
      const riskBusinessLoss =
        riskMatricesData?.find((riskMatrixData) => {
          return riskMatrixData?.key === businessLossKey;
        })?.values ?? [];
      const riskShe =
        riskMatricesData?.find((riskMatrixData) => {
          return riskMatrixData?.key === sheKey;
        })?.values ?? [];
      setBusinessLoss(riskBusinessLoss);
      setShe(riskShe);
    }
  }, [riskMatricesData]);

  const businessLossMatrix = useDataToGenerateMatrices(yLabels, xLabels, businessLoss);
  const sheMatrix = useDataToGenerateMatrices(yLabels, xLabels, she);

  return (
    <>
      <RiskAssessmentDrawerTable selectedTab={selectedTab} />
      <Box sx={reviewTabStyles}>
        <Stack
          direction="row"
          spacing={3}
          sx={{
            justifyContent: 'space-between',
            alignItems: 'baseline',
          }}
        >
          <Matrix
            data-testid="she-matrix"
            matrix={sheMatrix}
            text="SHE Risk Matrix"
            maxSize="320px"
          />
          <Matrix
            data-testid="business-loss-matrix"
            matrix={businessLossMatrix}
            text="Business Loss Risk Matrix"
            maxSize="320px"
          />
        </Stack>
      </Box>
    </>
  );
};
